// 404 Not Found Page
// Tells search engines this page should not be indexed and helps users find their way

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, Home } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";

const NotFoundPage = () => {
  // Directly set noindex on robots meta tag (Helmet has trouble overriding the static tag)
  useEffect(() => {
    const robotsMeta = document.querySelector('meta[name="robots"]');
    const previousRobots = robotsMeta?.getAttribute("content");
    if (robotsMeta) robotsMeta.setAttribute("content", "noindex, follow");
    return () => {
      if (robotsMeta && previousRobots) robotsMeta.setAttribute("content", previousRobots);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Page Not Found | Annelie Lily</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content="The page you're looking for doesn't exist. Explore yoga classes, holistic treatments, women's circles and retreats with Annelie Lily in Frome, Somerset." />
      </Helmet>
      <Navigation />
      <main className="min-h-[70vh] flex items-center justify-center bg-cream pt-24 pb-16 px-6">
        <div className="max-w-2xl mx-auto text-center" data-testid="not-found-page">
          <p className="text-gold text-sm tracking-widest uppercase font-medium mb-4">404 — Page Not Found</p>
          <h1 className="font-heading text-4xl md:text-5xl text-neutral-800 mb-6">
            This page seems to have wandered off
          </h1>
          <p className="text-base md:text-lg text-neutral-600 leading-relaxed mb-10">
            The page you're looking for may have moved or no longer exists. Take a breath, and let's guide you back to where you need to be.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/"
              className="btn-primary inline-flex items-center justify-center gap-2 px-8 py-3.5"
              data-testid="not-found-home-btn"
            >
              <Home className="w-4 h-4" /> Back to Home
            </Link>
            <Link
              to="/contact"
              className="btn-secondary inline-flex items-center justify-center gap-2 px-8 py-3.5"
              data-testid="not-found-contact-btn"
            >
              Contact Annelie <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="border-t border-gold/20 pt-10">
            <p className="text-sm text-neutral-500 uppercase tracking-widest mb-6">Or explore</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Link to="/yoga-classes" className="text-neutral-700 hover:text-gold transition-colors py-2" data-testid="not-found-link-yoga">Yoga Classes</Link>
              <Link to="/treatments" className="text-neutral-700 hover:text-gold transition-colors py-2" data-testid="not-found-link-treatments">Treatments</Link>
              <Link to="/womens-circles" className="text-neutral-700 hover:text-gold transition-colors py-2" data-testid="not-found-link-circles">Women's Circles</Link>
              <Link to="/retreats" className="text-neutral-700 hover:text-gold transition-colors py-2" data-testid="not-found-link-retreats">Retreats</Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default NotFoundPage;
